import type { NonNullableFormBuilder } from '@angular/forms';
import type { OnyxAmountForm } from '@onyx/angular';
import type { MergeDeep } from 'type-fest';
import { v7 as uuidv7 } from 'uuid';
import { NoteFormComponent } from '../../../../../common/components/forms/note-form/note-form.component';
import type { PreferencesService } from '../../../../../common/services/preferences.service';
import { uniqueBranchNamesValidator } from '../../../../../common/validators/unique-branch-names.validator';
import type {
  ContractorBranchForm,
  ContractorBranchFormGroup,
} from '../../contractor-form/contractor-branches-form/contractor-branch-form-modal/contractor-branch-form-modal.component';
import { ContractorBranchFormModalComponent } from '../../contractor-form/contractor-branches-form/contractor-branch-form-modal/contractor-branch-form-modal.component';
import { ContractorCompanyProfileFormComponent } from '../../contractor-form/contractor-company-profile-form/contractor-company-profile-form.component';
import { ContractorContactPersonsFormComponent } from '../../contractor-form/contractor-contact-persons-form/contractor-contact-persons-form.component';
import { ContractorDocumentsFormComponent } from '../../contractor-form/contractor-documents-form/contractor-documents-form.component';
import type { ContractorInvoiceDocumentsForm } from '../../contractor-form/contractor-invoice-documents-form/contractor-invoice-documents-form.component';
import { ContractorInvoiceDocumentsFormComponent } from '../../contractor-form/contractor-invoice-documents-form/contractor-invoice-documents-form.component';
import type { ContractorInvoiceForm } from '../../contractor-form/contractor-invoices-form/contractor-invoice-form/contractor-invoice-form.component';
import { ContractorInvoicesFormComponent } from '../../contractor-form/contractor-invoices-form/contractor-invoices-form.component';
import { ContractorPaymentFormComponent } from '../../contractor-form/contractor-payment-form/contractor-payment-form.component';
import { ContractorPointsOfInterestFormComponent } from '../../contractor-form/contractor-points-of-interest-form/contractor-points-of-interest-form.component';
import type { ContractorShippingTypeForm } from '../../contractor-form/contractor-shipping-type-form/contractor-shipping-type-form.component';
import type { Contractor, ShippingType } from '../interfaces/contractor';

export type ContractorFormGroup = ReturnType<
  typeof ContractorFormHelper.buildForm
>;
export type ContractorForm = ReturnType<ContractorFormGroup['getRawValue']>;

type ContractorShippingTypeFormDto = MergeDeep<
  Omit<ContractorShippingTypeForm, 'hasExistingType'>,
  {
    emails: string[] | null;
    internalPortal: ShippingType['internalPortal'] | null;
  }
>;

export type ContractorFormDto = MergeDeep<
  ContractorForm,
  {
    branches: Required<ContractorBranchForm>[];
    payment: {
      parameters: {
        cashDiscount:
          ContractorForm['payment']['parameters']['cashDiscount'] | null;
        tradeCreditLimit: OnyxAmountForm | null;
      };
    };
    invoiceDocuments: (Omit<ContractorInvoiceDocumentsForm, 'shippingType'> & {
      shippingType: ContractorShippingTypeFormDto;
    })[];
    invoices: (Omit<ContractorInvoiceForm, 'shippingType'> & {
      shippingType: ContractorShippingTypeFormDto;
    })[];
    pointsOfInterest: {
      uuid: string;
      gate: string | null;
    }[];
  }
>;

export class ContractorFormHelper {
  public static fromDto(dto: Contractor): ContractorForm {
    return {
      ...dto,
      companyProfile: {
        ...dto.companyProfile,
        types: dto.companyProfile.types?.map((type) => type.value) ?? null,
      },
      payment: {
        ...dto.payment,
        parameters: {
          ...dto.payment.parameters,
          tradeCreditLimit: dto.payment.parameters
            .tradeCreditLimit as ContractorForm['payment']['parameters']['tradeCreditLimit'],
          cashDiscount: dto.payment.parameters
            .cashDiscount as ContractorForm['payment']['parameters']['cashDiscount'],
        },
      },
      invoiceDocuments: dto.invoiceDocuments.map((invoiceDocument) => ({
        ...invoiceDocument,
        shippingType: {
          ...invoiceDocument.shippingType,
          emails: invoiceDocument.shippingType
            .emails as ContractorForm['invoiceDocuments'][number]['shippingType']['emails'],
          internalPortal: invoiceDocument.shippingType
            .internalPortal as ContractorForm['invoiceDocuments'][number]['shippingType']['internalPortal'],
          hasExistingType: true,
        },
      })),
      invoices: dto.invoices.map((invoice) => ({
        ...invoice,
        shippingType: {
          ...invoice.shippingType,
          emails: invoice.shippingType
            .emails as ContractorForm['invoices'][number]['shippingType']['emails'],
          internalPortal: invoice.shippingType
            .internalPortal as ContractorForm['invoices'][number]['shippingType']['internalPortal'],
          hasExistingType: true,
        },
      })),
      pointsOfInterest: {
        selectedPoints: dto.pointsOfInterest.map(
          (pointOfInterest) => pointOfInterest.pointOfInterest.uuid,
        ),
        pointsOfInterest: dto.pointsOfInterest.map((pointOfInterest) => ({
          pointOfInterest: pointOfInterest.pointOfInterest,
          gate: pointOfInterest.gate,
          uuid: pointOfInterest.pointOfInterest.uuid,
        })),
      },
      note: { content: dto.note?.content ?? null },
    };
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    const uuid = uuidv7();
    return fb.group({
      uuid: fb.control(uuid),
      companyProfile: ContractorCompanyProfileFormComponent.buildForm(fb),
      contactPersons: ContractorContactPersonsFormComponent.buildForm(fb),
      documents: ContractorDocumentsFormComponent.buildForm(fb),
      note: NoteFormComponent.buildForm(fb),
      payment: ContractorPaymentFormComponent.buildForm(fb, preferencesService),
      ...ContractorBranchFormModalComponent.buildForm(fb, { main: true })
        .controls,
      branches: fb.array<ContractorBranchFormGroup>(
        [],
        [uniqueBranchNamesValidator],
      ),
      pointsOfInterest: ContractorPointsOfInterestFormComponent.buildForm(fb),
      invoiceDocuments: ContractorInvoiceDocumentsFormComponent.buildForm(fb),
      invoices: ContractorInvoicesFormComponent.buildForm(fb),
    });
  }
}
